import { useLocale } from '@frontend/lang';
import { TransactionActorClient, TransactionClient, TransactionUserClient } from '@frontend/transaction/api';
import { TransactionActorType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useScreen } from '../useScreen';

interface ViewProps {
    manual: boolean;
    setManual: (manual: boolean) => void;
    pickedUpByManual: { first_name: string; last_name: string };
    setPickedUpByManual: (user: { first_name: string; last_name: string }) => void;
    pickedUpBy: string | null;
    setPickedUpBy: (userId: string | null) => void;
    comments: string;
    setComments: (comment: string) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const usePickUpBy = (): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: onContinue } = useScreen();
    const { locale } = useLocale();
    const [pickedUpBy, setPickedUpBy] = useState<string | null>(
        store?.data.receivers && store?.data.receivers.length > 0
            ? store?.data.receivers[0].user_id
            : store?.data.receiver?.user_id || store?.data.pickedUpBy || store?.data.transaction?.picked_up_by || store?.data.transaction?.user_id || null
    );
    const [manual, setManual] = useState<boolean>(false);
    const [pickedUpByManual, setPickedUpByManual] = useState<{ first_name: string; last_name: string }>({ first_name: '', last_name: '' });
    const [comments, setComments] = useState<string>(store?.data.comments || store?.data.transaction?.comments || '');

    useEffect(() => {
        if (!pickedUpBy && store?.data.transaction) {
            TransactionUserClient.fetchTransactionUsers({ transaction_id: store?.data.transaction.id, type: 'receiver' }).then((result) => {
                if (result.results.length > 0) {
                    setPickedUpBy(result.results[0].user_id);
                }
            });
        }
    }, [store?.data]);

    const onSubmit = async () => {
        if (!store || !store.data.transaction) return;
        if (manual) {
            const newUser = await UserClient.postUser(store.wizard.account_id, {
                first_name: pickedUpByManual.first_name,
                last_name: pickedUpByManual.last_name,
                language: locale,
                unique_identifier: crypto.randomUUID()
            });
            await TransactionActorClient.postTransactionActor(store.wizard.account_id, store.data.transaction.id, {
                user_id: newUser.id,
                type: TransactionActorType.PICKED_UP_BY
            });
            updateWizardData({
                pickedUpBy: newUser.id
            });
        } else if (pickedUpBy) {
            await TransactionActorClient.postTransactionActor(store.wizard.account_id, store.data.transaction.id, {
                user_id: pickedUpBy,
                type: TransactionActorType.PICKED_UP_BY
            }).catch(() => {
                // Silent fail
            });
            updateWizardData({
                pickedUpBy
            });
        }
        TransactionClient.patchTransaction(store.wizard.account_id, store.data.transaction.id, { comments })
            .then((res) => {
                updateWizardData({
                    transaction: res,
                    comments
                });
            })
            .catch(() => {
                // Silent fail
            });

        const nextStep = store?.steps.find(
            (step) =>
                step.screen === ScreenType.PICK_UP_BY && [ScreenTrigger.SUBMIT, ScreenTrigger.SUBMIT_FALSE, ScreenTrigger.SUBMIT_TRUE].includes(step.trigger)
        );
        onContinue(nextStep);
    };
    return {
        manual,
        setManual,
        pickedUpByManual,
        setPickedUpByManual,
        pickedUpBy,
        setPickedUpBy,
        comments,
        setComments,
        submitDisabled: pickedUpBy === null && (pickedUpByManual.first_name.trim() === '' || pickedUpByManual.last_name.trim() === ''),
        onSubmit,
        onBack
    };
};

export default usePickUpBy;
